import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import {
  ListingCalendarDay,
  ListingCalendarService,
  PlatformDiscount,
} from 'src/shared/services/listing-calendar.service';
import { ListingOptimizationSettingsService } from 'src/shared/services/listing-optimization-settings.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { PlatformSyncNotePipe } from 'src/shared/pipes/platform-sync-note.pipe';
import { Util } from 'src/shared/util/util';
import { ReservedWindow, RentalStrategyService, StrategyTargets, findReservedWindow, toIsoDate } from 'src/shared/services/rental-strategy.service';
import { Lease, LeaseService } from 'src/shared/services/lease.service';

import { LeaseLedgerPage } from 'src/pages/leases/lease-ledger/lease-ledger';

import { MOCK_ADDRESS, MockAddress } from '../../mock-property';
import {
  ACTIVE_PLATFORMS,
  DISCOUNT_REASON_LABELS,
  SurroundingWindow,
  buildSurroundingWindow,
  DEFAULT_TRACKED_QUERIES,
  ListingOptimization,
  PACE_STATE_LABELS,
  PLATFORM_LABELS,
  PROPERTY_BOUNDS,
  PaceState,
  PLATFORM_BY_SLUG,
  PerPlatformPrice,
  RankAdjustment,
  RankCell,
  TrackedQuery,
  buildListingOptimization,
  buildPaceState,
  buildPlatformPrices,
  buildPriceLabsSuggested,
  buildRankAdjustment,
  buildRankBreakdown,
  clampToBounds,
  formatCents,
} from '../day-detail-mocks';

@Component({
  selector: 'app-optimizer-day-detail',
  templateUrl: './day-detail.page.html',
  styleUrls: ['./day-detail.page.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, TidyUiComponentsModule, SharedModule, PlatformSyncNotePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OptimizerDayDetailPage implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly destroyRef = inject(DestroyRef);
  readonly rightSidePanelService = inject(RightSidePanelService);
  readonly windowService = inject(WindowService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly rentalStrategyService = inject(RentalStrategyService);
  private readonly leaseService = inject(LeaseService);
  private readonly listingCalendar = inject(ListingCalendarService);
  private readonly listingSettings = inject(ListingOptimizationSettingsService);
  private readonly util = inject(Util);
  private panelClosedSubscription: Subscription;

  // The dialog container assigns the `data` arg from openRightSidePanel onto this property.
  data: { day?: ListingCalendarDay; addressId?: number; addressName?: string } = {};

  // The address this date belongs to — falls back to MOCK_ADDRESS when the
  // caller didn't pass one (e.g. deep link without an optimizer-section parent).
  address: MockAddress = MOCK_ADDRESS;

  // Reservation state for the selected date. `reservedWindows` is the full
  // set for the address; `activeCoveringWindow` is whichever one (if any)
  // covers the selected date — used to render the "part of a hold" context
  // line and to resolve the Remove action to the right window.
  reservedWindows: ReservedWindow[] = [];
  activeCoveringWindow: ReservedWindow | null = null;
  activeCoveringRangeLabel = '';
  // True only when the covering window spans more than this single date —
  // a same-day window is just this date's own availability record, not a
  // multi-day "hold" worth surfacing as a removable range.
  activeCoveringIsMultiDay = false;
  targets: StrategyTargets | null = null;

  // Set when the selected date falls inside an active lease's term for this
  // address — the lease (legal agreement) is what's actually holding the
  // calendar, so the panel leads with a read-only lease card and suppresses
  // the availability pills / block-off checkbox / tier switcher / pipeline
  // cards, the same way a fully-booked day does.
  leaseVm: {
    lease: Lease;
    tenantName: string;
    monthlyRentLabel: string;
    termLabel: string;
    endDateLabel: string;
  } | null = null;

  // ---------- Single-tier listing collapse ----------
  // "Single-tier" = the LISTING (not this date) has exactly one of
  // short_term_enabled / longer_term_enabled on. Both true (or both false,
  // per spec — don't special-case that combo) keeps the full dual-tier panel.
  singleTier = false;
  singleTierKind: 'short' | 'longer' | null = null;
  // Precomputed card numbers so the template never branches/computes —
  // dual-tier keeps the historical numbers (1 = Available for, short
  // pipeline 2-7, longer-term view 2); single-tier and real accounts
  // renumber from 1 since card 1 (Available for) is hidden for them.
  private static readonly CARD_NUM_FROM_1 = { priceLimits: 1, priceLabs: 2, rank: 3, platform: 4, stay: 5, listing: 6, monthlyTarget: 1 };
  private static readonly CARD_NUM_FROM_2 = { priceLimits: 2, priceLabs: 3, rank: 4, platform: 5, stay: 6, listing: 7, monthlyTarget: 2 };
  cardNum = OptimizerDayDetailPage.CARD_NUM_FROM_2;

  // ---------- "Available for" card ----------
  availableShort = true;
  availableLonger = true;
  rangeApplyOpen = false;
  readonly rangeApplyForm = this.fb.group({
    endDate: [''],
  });

  // ---------- Tier switcher (short-term vs longer-term view) ----------
  viewTier: 'short' | 'longer' = 'short';

  // Longer-term view-model — precomputed once per date/tier load so the
  // template never calls a method to render it (repo rule: no template fns).
  longerVm: {
    midTargetCents: number;
    midFloorCents: number;
    midNightlyEquivCents: number;
    longTargetCents: number;
    longFloorCents: number;
    longNightlyEquivCents: number;
  } | null = null;

  readonly platformLabels = PLATFORM_LABELS;
  readonly platforms = ACTIVE_PLATFORMS;
  readonly paceLabels = PACE_STATE_LABELS;

  // Demo accounts keep the full mock pipeline. Real accounts read/write the
  // listing-calendar + per-date settings backend (gotidy #8271).
  //
  // This gates layers 3 (Rank Tracker) and 6 (Listing Optimization) only — both
  // are built entirely from day-detail-mocks, so a real account has nothing to
  // show. Layer 5 (Minimum Stay) is NOT gated: its per-date min-stay override
  // writes through address-settings-by-date and is live for real accounts.
  isRealMode = false;
  get comingSoonLayers(): boolean { return this.isRealMode; }

  private addressId: number | null = null;
  /** Server-resolved effective nightly price (override → PriceLabs → base), cents. */
  private serverEffectiveCents: number | null = null;

  // Layer 1 account-default pills. Demo shows the mock PROPERTY_BOUNDS; real
  // shows the optimizer-category address-settings rows ('—' when unset).
  readonly bounds = PROPERTY_BOUNDS;
  minPillLabel = formatCents(PROPERTY_BOUNDS.minCents);
  maxPillLabel = formatCents(PROPERTY_BOUNDS.maxCents);

  // Tracked queries — mock state, persists for the lifetime of this panel session.
  trackedQueries: TrackedQuery[] = DEFAULT_TRACKED_QUERIES.map(q => ({ ...q }));
  customizeOpen = false;
  // Stable items array for the per-row <tidy-checkbox> in the customize popup —
  // declared once so the array reference doesn't churn on every CD cycle.
  readonly trackedQueryCheckboxItems = [{ viewValue: '', value: 'on' }];

  // Single-tier "Block off this date" checkbox — stable items array, same
  // reasoning as trackedQueryCheckboxItems above.
  readonly blockOffCheckboxItems = [{ viewValue: 'Block off this date', value: 'block' }];

  // Real-mode block state, persisted through address-settings-by-date. Distinct
  // from the demo-only `isBlocked` getter below, which is derived from the two
  // availability pills and never round-trips to a backend.
  blockedOff = false;

  // Layer 1 inputs.
  readonly overrideToggleItems = [{ viewValue: 'Add manual price override for this date', value: 'override' }];
  showOverride = false;
  readonly priceForm: FormGroup = this.fb.group({
    minPrice: [''],
    maxPrice: [''],
    override: [''],
  });

  // Derived per-day state.
  selectedDate = '';
  selectedAvailable = true;
  // Past dates are read-only — the channels reject block/price writes for them
  // ("date is in the past"), so guard the write handlers (Trello 18343). The
  // grid already blocks opening a past day; this is a backstop.
  get isPastDate(): boolean {
    if (!this.selectedDate) { return false; }
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return this.selectedDate < todayKey;
  }
  // Gates the body so neither branch (optimization pipeline / "already booked")
  // renders until we've resolved the day from the seed. Without it, the panel
  // paints the pipeline with the default `selectedAvailable = true` before the
  // async setup runs, flashing the wrong view on booked nights.
  ready = false;

  // Layer 2.
  priceLabsCents = 0;
  priceLabsModel = '';
  priceLabsDeltaPct = 0;
  /** False when a real date has no stored PriceLabs suggestion snapshot yet. */
  hasSuggestion = true;

  // Layer 3.
  paceState: PaceState = 'on-track';
  rankCells: RankCell[] = [];
  rankAdjustment: RankAdjustment | null = null;
  postRankCents = 0;

  // Layer 4. `platformDiscounts` is the raw server state (one row per connected
  // platform); `platformPrices` is what the template renders.
  platformPrices: PerPlatformPrice[] = [];
  private platformDiscounts: PlatformDiscount[] = [];

  // Layer 5.
  surroundingWindow: SurroundingWindow | null = null;
  readonly stayForm: FormGroup = this.fb.group({
    minNights: [2],
  });
  showStayOverride = false;
  readonly stayOverrideToggleItems = [{ viewValue: 'Add manual minimum stay override for this date', value: 'stay-override' }];

  onShowStayOverrideChange(checked: boolean): void {
    this.showStayOverride = checked;
    if (this.isRealMode && !checked) {
      // Unchecking discards the per-date min-stay: clear the input without
      // emitting (so the debounced save doesn't fire) and clear it server-side.
      this.stayForm.get('minNights')?.setValue('', { emitEvent: false });
      void this.clearStayOverride();
    }
    this.cdr.markForCheck();
  }

  // Layer 6.
  listingOpt: ListingOptimization | null = null;

  async ngOnInit(): Promise<void> {
    // The data may arrive via the dialog-container's componentRef.instance.data
    // assignment OR via dialog-params storage (set by navigateTo on mobile).
    let day: ListingCalendarDay | undefined = this.data?.day;
    let addressId: number | undefined = this.data?.addressId;
    let addressName = this.data?.addressName;
    if (!day) {
      const params = await this.storage.retrieve('dialog-params');
      day = params?.day;
      addressId = addressId ?? params?.addressId;
      addressName = addressName ?? params?.addressName;
    }
    if (!day) {return;}

    this.isRealMode = !this.demoBlock.isDemo && addressId !== undefined && addressId !== null;
    this.addressId = addressId ?? null;

    if (addressId !== null && addressId !== undefined) {
      this.address = { ...MOCK_ADDRESS, id: addressId, display_name: addressName ?? MOCK_ADDRESS.display_name };
    }

    // Resolve availability from the seed and reveal the body up front so the
    // correct branch renders on first paint — booked nights show the "already
    // booked" message immediately instead of flashing the pipeline first.
    this.selectedDate = day.date;
    this.selectedAvailable = !day.isBooked;
    this.ready = true;
    this.cdr.markForCheck();

    if (this.isRealMode) {
      this.cardNum = OptimizerDayDetailPage.CARD_NUM_FROM_1;
      await this.buildRealState(day);
    } else {
      // Demo-only layer: reserved windows, tier targets and leases are all
      // served by the mock rental-strategy/lease services, so real accounts
      // skip it entirely and read the listing-calendar instead.
      await this.loadReservationState(day.date);
      // Default the tier switcher to whichever tier is actually available for
      // this date — falls back to longer-term when short-term is off. Skipped
      // in single-tier mode: loadReservationState already pinned viewTier to
      // the listing's one enabled tier, and there's no switcher to default.
      if (!this.singleTier) {
        this.viewTier = this.availableShort ? 'short' : 'longer';
      }
      this.buildState(day);
    }
    this.publishHeader();
    this.watchPriceChanges();
    this.watchStayChanges();
  }

  // Loads reserved windows + targets + leases for the address and derives the
  // availability-pill state (and covering-window context line) for the
  // selected date. Demo-only — see the ngOnInit branch.
  private async loadReservationState(date: string): Promise<void> {
    const [windows, targets, leases] = await Promise.all([
      this.rentalStrategyService.getReservedWindows(this.address.id),
      this.rentalStrategyService.getTargets(this.address.id),
      this.leaseService.getLeasesForAddress(this.address.id),
    ]);
    this.reservedWindows = windows;
    this.targets = targets;
    this.applySingleTierState(targets);
    this.applyAvailabilityForDate(date);
    this.buildLongerVm();
    this.buildLeaseVm(date, leases);
  }

  // A signed lease covering this date is what's actually holding the
  // calendar — precompute its read-only card once here so the template
  // never touches the Lease model directly.
  private buildLeaseVm(date: string, leases: Lease[]): void {
    const lease = leases.find(l => date >= l.start_date && date <= l.end_date) ?? null;
    if (!lease) { this.leaseVm = null; return; }
    this.leaseVm = {
      lease,
      tenantName: lease.tenant_name,
      monthlyRentLabel: `$${lease.monthly_rent.toLocaleString('en-US')}/mo`,
      termLabel: `${this.formatShortDate(lease.start_date)} – ${this.formatShortDate(lease.end_date)}`,
      endDateLabel: this.formatLeaseEndDate(lease.end_date),
    };
  }

  private formatLeaseEndDate(date: string): string {
    const d = new Date(`${date}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // Single-tier is a LISTING-level property (targets), not per-date — so this
  // only needs the targets, and it also pins viewTier to the one enabled tier
  // since the switcher is hidden in that mode.
  private applySingleTierState(targets: StrategyTargets): void {
    this.singleTier = targets.short_term_enabled !== targets.longer_term_enabled;
    this.singleTierKind = !this.singleTier
      ? null
      : targets.short_term_enabled
      ? 'short'
      : 'longer';
    if (this.singleTierKind) {
      this.viewTier = this.singleTierKind;
    }
    this.cardNum = this.singleTier
      ? OptimizerDayDetailPage.CARD_NUM_FROM_1
      : OptimizerDayDetailPage.CARD_NUM_FROM_2;
  }

  // Derives the two pill states + the "part of a hold" context line for the
  // viewed date from whichever window (if any) covers it. No window means
  // both tiers are available by default.
  private applyAvailabilityForDate(date: string): void {
    const window = findReservedWindow(date, this.reservedWindows);
    this.activeCoveringWindow = window;
    if (!window) {
      this.availableShort = true;
      this.availableLonger = true;
      this.activeCoveringRangeLabel = '';
      this.activeCoveringIsMultiDay = false;
      return;
    }
    switch (window.tier) {
      case 'short':
        this.availableShort = true;
        this.availableLonger = false;
        break;
      case 'longer':
        this.availableShort = false;
        this.availableLonger = true;
        break;
      case 'none':
        this.availableShort = false;
        this.availableLonger = false;
        break;
    }
    this.activeCoveringRangeLabel = this.formatReservedRange(window);
    this.activeCoveringIsMultiDay = window.start_date !== date || window.end_date !== date;
  }

  private formatReservedRange(window: ReservedWindow): string {
    const start = this.formatShortDate(window.start_date);
    if (!window.end_date) {return `${start} onward`;}
    return `${start} – ${this.formatShortDate(window.end_date)}`;
  }

  private formatShortDate(date: string): string {
    const d = new Date(`${date}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  // Precomputes the read-only Longer-term view — monthly target rows (with
  // an effective-nightly equivalent) for both the mid- and long-term targets
  // — so the template never calls a method.
  private buildLongerVm(): void {
    if (!this.targets) {this.longerVm = null; return;}
    const t = this.targets;
    this.longerVm = {
      midTargetCents: t.mid_monthly_target,
      midFloorCents: t.mid_monthly_floor,
      midNightlyEquivCents: Math.round(t.mid_monthly_target / 30),
      longTargetCents: t.long_monthly_target,
      longFloorCents: t.long_monthly_floor,
      longNightlyEquivCents: Math.round(t.long_monthly_target / 30),
    };
  }

  // ---------- Demo/mock state (unchanged pipeline) ----------

  private buildState(day: ListingCalendarDay): void {
    this.selectedDate = day.date;
    this.selectedAvailable = !day.isBooked;
    const cents = day.price ?? 0;

    const pl = buildPriceLabsSuggested(day.date, cents);
    this.priceLabsCents = pl.cents;
    this.priceLabsModel = pl.model;
    this.priceLabsDeltaPct = pl.deltaFromMidpointPct;

    this.paceState = buildPaceState(day.date);
    this.rankCells = buildRankBreakdown(day.date, this.trackedQueries);
    this.rankAdjustment = buildRankAdjustment(day.date, this.rankCells, this.paceState);
    this.postRankCents = this.priceLabsCents + Math.round(this.priceLabsCents * (this.rankAdjustment.pct / 100));

    this.platformPrices = buildPlatformPrices(day.date, this.effectivePrePlatformCents);

    const win = buildSurroundingWindow(day.date, this.selectedAvailable);
    this.surroundingWindow = win;
    this.stayForm.patchValue({
      minNights: win.suggestedMinNights,
    }, { emitEvent: false });

    this.listingOpt = buildListingOptimization(day.date);

    // Seed the price-limit inputs from the property bounds so the user can
    // edit them in place (autosave wires through onPriceChange).
    this.priceForm.patchValue({
      minPrice: this.formatCentsForInput(this.bounds.minCents),
      maxPrice: this.formatCentsForInput(this.bounds.maxCents),
    }, { emitEvent: false });

    this.cdr.markForCheck();
  }

  // ---------- Real state (listing-calendar day detail + optimizer defaults) ----------

  private async buildRealState(seed: ListingCalendarDay): Promise<void> {
    this.selectedDate = seed.date;
    this.selectedAvailable = !seed.isBooked;
    this.blockedOff = seed.isBlocked;
    this.serverEffectiveCents = seed.price;
    this.hasSuggestion = false;

    const [detailResult, defaultsResult] = await Promise.allSettled([
      this.listingCalendar.getDay(this.addressId!, seed.date),
      this.listingSettings.getOptimizerDefaults(this.addressId!),
    ]);

    if (detailResult.status === 'fulfilled') {
      const detail = detailResult.value;
      this.selectedAvailable = !detail.isBooked;
      this.blockedOff = detail.isBlocked;
      this.serverEffectiveCents = detail.price;
      this.hasSuggestion = detail.suggestedPrice !== null;
      this.priceLabsCents = detail.suggestedPrice ?? 0;
      if (detail.overridePrice !== null) {
        this.showOverride = true;
        this.priceForm.patchValue(
          { override: this.formatCentsForInput(detail.overridePrice) },
          { emitEvent: false }
        );
      }
      // Per-date min-stay override (gotidy #8271 — only min_stay is stored per date).
      if (detail.minStay !== null) {
        this.showStayOverride = true;
        this.stayForm.patchValue({ minNights: detail.minStay }, { emitEvent: false });
      }
      this.platformDiscounts = detail.platformDiscounts;
    }

    if (defaultsResult.status === 'fulfilled') {
      const defaults = defaultsResult.value;
      this.minPillLabel = defaults.minPrice !== null ? formatCents(defaults.minPrice) : '—';
      this.maxPillLabel = defaults.maxPrice !== null ? formatCents(defaults.maxPrice) : '—';
    } else {
      this.minPillLabel = '—';
      this.maxPillLabel = '—';
    }

    this.refreshRealPlatformRows();
    this.cdr.markForCheck();
  }

  /**
   * Real per-platform discount rows, straight from the day-detail payload. The
   * backend already resolved which lever won per platform and what it costs, so
   * nothing is recomputed here.
   *
   * Rows exist only for platforms with a connected listing, so an address with
   * none yields an empty array — `hasPlatformRows` drives the empty state
   * rather than rendering a fake 0% row per platform.
   */
  private refreshRealPlatformRows(): void {
    const cents = this.effectivePrePlatformCents;
    this.platformPrices = this.platformDiscounts.map(d => {
      const platform = PLATFORM_BY_SLUG[d.platform];
      return {
        platform,
        label: PLATFORM_LABELS[platform],
        // Re-derived from the CURRENT effective price rather than the server's
        // `final_price`, so an override the user is still typing stays
        // consistent across the panel. The two agree once the save lands and
        // the day refetches, since final_price is exactly this product.
        finalCents: Math.round(cents * (1 - d.discountPct / 100)),
        discountPct: d.discountPct,
        reason: d.reason ? DISCOUNT_REASON_LABELS[d.reason] : '',
        status: d.status,
      };
    });
  }

  get hasPlatformRows(): boolean {
    return this.platformPrices.length > 0;
  }

  private publishHeader(): void {
    this.rightSidePanelService.setDialogPageTitle(this.formatDayHeader(this.selectedDate));
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    // No header help icon — it linked nowhere, so it's cleared rather than
    // left as a dead question mark in the dialog corner.
    this.rightSidePanelService.setHeaderIcons([]);
  }

  private buildSubtitle(): string {
    if (this.leaseVm) {return `Leased through ${this.leaseVm.endDateLabel}`;}
    if (!this.selectedAvailable) {return 'Booked';}
    if (this.isBlocked) {return 'Blocked';}
    if (this.singleTier) {
      return this.singleTierKind === 'longer' ? 'Available for longer-term' : 'Available';
    }
    if (this.availableShort && !this.availableLonger) {return 'Available for short-term';}
    if (!this.availableShort && this.availableLonger) {return 'Available for longer-term';}
    return 'Available for any stay';
  }

  // Public getters consumed by the mobile <tidy-top-nav> binding. The desktop
  // dialog container already calls publishHeader() to populate its own
  // header — these mirror the same values so mobile reads the same source.
  get dayTitle(): string {
    return this.formatDayHeader(this.selectedDate);
  }
  get daySubtitle(): string {
    return this.buildSubtitle();
  }

  private watchPriceChanges(): void {
    this.priceForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onPriceChange());
    if (this.isRealMode) {
      // Persist the manual override after the user stops typing. The write is
      // range-of-one; null clears the stored override.
      this.priceForm.get('override')!.valueChanges
        .pipe(debounceTime(800), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => void this.saveOverride());
    }
  }

  private watchStayChanges(): void {
    if (!this.isRealMode) { return; }
    // Persist the per-date min-stay after the user stops typing (range-of-one).
    this.stayForm.get('minNights')!.valueChanges
      .pipe(debounceTime(800), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.saveStayOverride());
  }

  goToBookingSettings(): void {
    this.rightSidePanelService.closeRightSidePanel();
    setTimeout(() => this.router.navigate(['/optimizer']), 220);
  }

  // Deep-links from the leased-day card to the tenant's lease ledger — same
  // idiom as payment-detail.ts's goToLease/goToPage (navigate within the
  // already-open right-side panel on desktop, push a new page on mobile).
  async goToLeaseLedger(): Promise<void> {
    if (!this.leaseVm) { return; }
    await this.goToPage('lease-ledger', { leaseId: this.leaseVm.lease.id }, LeaseLedgerPage);
  }

  private async goToPage(url: string, params: any, component: any): Promise<void> {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  openCustomize(): void {
    if (this.demoBlock.block()) { return; }
    this.customizeOpen = true;
    this.cdr.markForCheck();
  }

  closeCustomize(): void {
    this.customizeOpen = false;
    if (this.selectedDate && !this.isRealMode) {
      this.rankCells = buildRankBreakdown(this.selectedDate, this.trackedQueries);
      this.rankAdjustment = buildRankAdjustment(this.selectedDate, this.rankCells, this.paceState);
      this.postRankCents = this.priceLabsCents + Math.round(this.priceLabsCents * (this.rankAdjustment.pct / 100));
      this.platformPrices = buildPlatformPrices(this.selectedDate, this.effectivePrePlatformCents);
      this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    }
    this.cdr.markForCheck();
  }

  toggleQuery(q: TrackedQuery): void { q.enabled = !q.enabled; }

  // Flips one availability pill for THIS DATE only and persists it via
  // setAvailability(date, date, ...). Disabled entirely when the date is
  // already booked (see the template's [disabled] binding). Demo-only — real
  // accounts block dates through onBlockedOffChange below.
  async toggleAvailability(tier: 'short' | 'longer'): Promise<void> {
    if (this.demoBlock.block() || !this.selectedAvailable || this.leaseVm) { return; }
    const nextState = {
      short: tier === 'short' ? !this.availableShort : this.availableShort,
      longer: tier === 'longer' ? !this.availableLonger : this.availableLonger,
    };
    await this.rentalStrategyService.setAvailability(this.address.id, this.selectedDate, this.selectedDate, nextState);
    await this.loadReservationState(this.selectedDate);
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    this.cdr.markForCheck();
  }

  onBlockedOffChange(checked: boolean): void {
    if (this.demoBlock.block()) { return; }
    if (this.isPastDate) { return; }
    this.blockedOff = checked;
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    this.cdr.markForCheck();
    if (this.isRealMode && this.addressId !== null) {
      // `true` blocks the date; null removes the stored block (unblock).
      void this.listingCalendar
        .setDateSettings(this.addressId, this.selectedDate, this.selectedDate, {
          isBlocked: checked ? true : null,
        })
        .catch(() => {
          this.blockedOff = !checked;
          this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
          this.cdr.markForCheck();
          this.util.showError('Couldn’t update the block for this date. Please try again.');
        });
    }
  }

  // Single-tier mode has no availability pills, so this checkbox is the only
  // way to block/unblock the date. Checked = both tiers off (a 'none' window,
  // or any state reducing to fully blocked). Unchecking restores both tiers
  // to true — the listing-level flag is what actually gates which tier reads
  // as "available" afterward, per applyAvailabilityForDate.
  async toggleBlockOff(checked: boolean): Promise<void> {
    if (this.demoBlock.block() || !this.selectedAvailable || this.leaseVm) { return; }
    await this.rentalStrategyService.setAvailability(this.address.id, this.selectedDate, this.selectedDate, {
      short: !checked,
      longer: !checked,
    });
    await this.loadReservationState(this.selectedDate);
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    this.cdr.markForCheck();
  }

  // Removes the whole covering window (not just this date) — used by the
  // "part of a hold" context line's Remove action.
  async removeCoveringWindow(): Promise<void> {
    if (this.demoBlock.block() || !this.activeCoveringWindow) { return; }
    await this.rentalStrategyService.removeReservedWindow(this.address.id, this.activeCoveringWindow.id);
    await this.loadReservationState(this.selectedDate);
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    this.cdr.markForCheck();
  }

  openRangeApply(): void {
    if (this.demoBlock.block()) { return; }
    this.rangeApplyOpen = true;
    this.rangeApplyForm.reset({ endDate: '' });
    this.cdr.markForCheck();
  }

  closeRangeApply(): void {
    this.rangeApplyOpen = false;
    this.cdr.markForCheck();
  }

  // Applies the CURRENT pill state (short/longer on or off, as already
  // toggled for this date) across [selectedDate..endDate].
  async submitRangeApply(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    const { endDate } = this.rangeApplyForm.getRawValue();
    if (!endDate) { return; }
    // tidy-datepicker writes a JS Date at runtime despite the string control.
    const end = toIsoDate(endDate as string | Date);
    await this.rentalStrategyService.setAvailability(this.address.id, this.selectedDate, end, {
      short: this.availableShort,
      longer: this.availableLonger,
    });
    this.rangeApplyOpen = false;
    await this.loadReservationState(this.selectedDate);
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    this.cdr.markForCheck();
  }

  setViewTier(tier: 'short' | 'longer'): void {
    this.viewTier = tier;
    this.cdr.markForCheck();
  }

  onPriceChange(): void {
    if (this.demoBlock.block()) { return; }
    if (this.isRealMode) {
      this.refreshRealPlatformRows();
    } else {
      this.platformPrices = buildPlatformPrices(this.selectedDate, this.effectivePrePlatformCents);
    }
    this.rightSidePanelService.setDialogPageSubtitle(this.buildSubtitle());
    this.cdr.markForCheck();
  }

  onShowOverrideChange(checked: boolean): void {
    if (this.demoBlock.block()) { return; }
    this.showOverride = checked;
    if (this.isRealMode && !checked) {
      // Unchecking discards the override: clear the input (no valueChanges
      // save — the control emits through the debounce with an empty value,
      // which saveOverride treats as a clear) and clear the stored value now.
      this.priceForm.get('override')?.setValue('', { emitEvent: false });
      void this.clearOverride();
    }
    this.onPriceChange();
  }

  private async saveOverride(): Promise<void> {
    if (!this.isRealMode || this.addressId === null || !this.showOverride) { return; }
    if (this.isPastDate) { return; }
    const cents = this.parsePriceToCents(this.priceForm.get('override')?.value);
    // Whole dollars only — the backend 400s on non-multiples of 100.
    const wholeDollarCents = cents === null ? null : Math.round(cents / 100) * 100;
    try {
      await this.listingCalendar.setDateSettings(this.addressId, this.selectedDate, this.selectedDate, {
        overridePrice: wholeDollarCents,
      });
      await this.refreshEffectivePrice();
    } catch {
      this.util.showError('Couldn’t save the price override. Please use a whole dollar amount and try again.');
    }
  }

  private async clearOverride(): Promise<void> {
    if (this.addressId === null) { return; }
    try {
      await this.listingCalendar.setDateSettings(this.addressId, this.selectedDate, this.selectedDate, {
        overridePrice: null,
      });
      await this.refreshEffectivePrice();
    } catch {
      this.util.showError('Couldn’t remove the price override. Please try again.');
    }
  }

  private async saveStayOverride(): Promise<void> {
    if (!this.isRealMode || this.addressId === null || !this.showStayOverride) { return; }
    if (this.isPastDate) { return; }
    const nights = this.parseNights(this.stayForm.get('minNights')?.value);
    try {
      await this.listingCalendar.setDateSettings(this.addressId, this.selectedDate, this.selectedDate, {
        minStay: nights,
      });
    } catch {
      this.util.showError('Couldn’t save the minimum stay for this date. Please try again.');
    }
  }

  private async clearStayOverride(): Promise<void> {
    if (this.addressId === null) { return; }
    try {
      await this.listingCalendar.setDateSettings(this.addressId, this.selectedDate, this.selectedDate, {
        minStay: null,
      });
    } catch {
      this.util.showError('Couldn’t remove the minimum stay for this date. Please try again.');
    }
  }

  /** Parses a nights input to a positive whole number, or null when blank/invalid. */
  private parseNights(value: any): number | null {
    if (value === null || value === undefined || value === '') { return null; }
    const n = Math.round(Number(value));
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /** Re-reads the day so the displayed target price is the server-resolved effective price. */
  private async refreshEffectivePrice(): Promise<void> {
    if (this.addressId === null) { return; }
    try {
      const detail = await this.listingCalendar.getDay(this.addressId, this.selectedDate);
      this.serverEffectiveCents = detail.price;
      this.hasSuggestion = detail.suggestedPrice !== null;
      this.priceLabsCents = detail.suggestedPrice ?? 0;
      this.onPriceChange();
    } catch {
      // Keep the locally-computed price; the calendar re-fetches on close anyway.
    }
  }

  // tidy-input with currencyMask emits a "$1,234.56"-style string; parse the
  // numeric part so we can use it as cents downstream.
  private parsePriceToCents(value: any): number | null {
    if (value === null || value === undefined || value === '') {return null;}
    const numeric = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric * 100) : null;
  }

  private formatCentsForInput(cents: number): string {
    return String(Math.round(cents / 100));
  }

  // Effective bounds let the user's edits in the Min/Max price inputs
  // override the property defaults — that drives the running computation
  // and the Target Price in the dialog subtitle.
  private get effectiveBounds() {
    const min = this.parsePriceToCents(this.priceForm.get('minPrice')?.value);
    const max = this.parsePriceToCents(this.priceForm.get('maxPrice')?.value);
    return {
      minCents: min ?? this.bounds.minCents,
      maxCents: max ?? this.bounds.maxCents,
      defaultMinNights: this.bounds.defaultMinNights,
      defaultMaxNights: this.bounds.defaultMaxNights,
    };
  }

  private parseOverrideToDollars(): number | null {
    if (!this.showOverride) {return null;}
    const cents = this.parsePriceToCents(this.priceForm.get('override')?.value);
    return cents === null ? null : cents / 100;
  }

  get effectivePrePlatformCents(): number {
    const dollars = this.parseOverrideToDollars();
    if (this.isRealMode) {
      // Server resolves override → PriceLabs → base; while the user is typing
      // an override, mirror it locally so the target price tracks the input.
      if (dollars !== null) { return Math.round(dollars) * 100; }
      return this.serverEffectiveCents ?? 0;
    }
    if (dollars !== null) {
      return clampToBounds(Math.round(dollars * 100), this.effectiveBounds);
    }
    return clampToBounds(this.postRankCents, this.effectiveBounds);
  }

  get hasOverride(): boolean {
    return this.parseOverrideToDollars() !== null;
  }

  // Both availability pills off — the short-term pipeline reads as blocked
  // (replaces the old standalone "Block off this date" checkbox). The pills are
  // demo-only (see the ngOnInit branch), so real accounts read the stored
  // listing-calendar flag instead — otherwise they'd never render as blocked.
  get isBlocked(): boolean {
    if (this.isRealMode) {return this.blockedOff;}
    return !this.availableShort && !this.availableLonger;
  }

  rankFor(queryId: string, platform: 'airbnb' | 'vrbo' | 'booking'): RankCell | undefined {
    return this.rankCells.find(c => c.query === queryId && c.platform === platform);
  }

  tierFor(position: number | null): 'great' | 'good' | 'fair' | 'poor' {
    if (position === null) {return 'poor';}
    if (position <= 5) {return 'great';}
    if (position <= 15) {return 'good';}
    if (position <= 30) {return 'fair';}
    return 'poor';
  }

  paceClass(): 'good' | 'mid' | 'bad' {
    switch (this.paceState) {
      case 'ahead':
      case 'strong-demand':
        return 'good';
      case 'on-track':
        return 'mid';
      case 'behind':
        return 'bad';
    }
  }

  adjustmentClass(pct: number): 'positive' | 'negative' | 'neutral' {
    if (pct > 0) {return 'positive';}
    if (pct < 0) {return 'negative';}
    return 'neutral';
  }

  fmt(cents: number): string { return formatCents(cents); }

  formatDayHeader(date: string): string {
    if (!date) {return '';}
    const d = new Date(`${date}T00:00:00`);
    // Locale-driven parts so we get "Wed June 10" without comma/year. Using
    // toLocaleDateString with multiple keys inserts locale-specific commas;
    // assembling from parts keeps it tight for the mobile top-nav title.
    const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
    const month = d.toLocaleDateString(undefined, { month: 'long' });
    const day = d.toLocaleDateString(undefined, { day: 'numeric' });
    return `${weekday} ${month} ${day}`;
  }

  get enabledQueries(): TrackedQuery[] {
    return this.trackedQueries.filter(q => q.enabled);
  }
}
